import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';

export const GET_SESSIONS_LIST = 'GET_SESSIONS_LIST';
export const GET_SESSION_DETAILS = 'GET_SESSION_DETAILS';
export const UPDATE_GROUND_TRUTH = 'UPDATE_GROUND_TRUTH';
export const UPDATE_GROUND_TRUTH_SUCCESS = 'UPDATE_GROUND_TRUTH_SUCCESS';

const initialState = {
  sessions: [],
  devices: [],
  sessionDetails: {},
  serviceStatus: {},
  actionInProgress: false,
  actionCompleted: false,
  error: {
    message: '',
  },
  failedRequests: {},
  activeSessions: [],
  dataInterval: 7, // in days
  pageNumber: 1,
};

const fetchPropertyValue = (action, propertyName) => {
  return get(action, `payload.data.${propertyName}`, '');
};

export const groundTruthUpdatesSlice = createSlice({
  name: 'groundTruthUpdates',
  initialState,
  reducers: {
    setPageNumber: (state, action) => {
      state.pageNumber = get(action, 'payload.pageNumber', 1);
    },
    getSessionsSuccess: (state, action) => {
      state.sessions = fetchPropertyValue(action, 'sessions');
      state.error = '';
      state.actionInProgress = false;
    },
    getSessionsInProgress: (state) => {
      state.error = '';
      state.actionInProgress = true;
    },
    getSessionsFailed: (state) => {
      state.error = {
        message: 'Failed to fetch the device list.',
      };
      state.actionInProgress = false;
    },
    getSessionDetailsSuccess: (state, action) => {
      state.sessionDetails = fetchPropertyValue(action, 'sessionDetails');
      state.actionInProgress = false;
    },
    getSessionDetailsInProgress: (state) => {
      state.actionInProgress = true;
    },
    getSessionDetailsFailed: (state) => {
      state.sessionDetails = {};
      state.error = {
        message: 'Failed to fetch the session details.',
      };
      state.actionInProgress = false;
    },
    getServiceStatusSuccess: (state, action) => {
      state.serviceStatus = get(action, 'payload', {});
      state.actionInProgress = false;
    },
    getServiceStatusInProgress: (state) => {
      state.actionInProgress = true;
    },
    getServiceStatusFailed: (state) => {
      state.error = {
        message: 'Failed to fetch the service status.',
      };
      state.actionInProgress = false;
    },
    updateGroundTruthSuccess: (state, action) => {
      const result = get(action, 'payload.data', {});
      if (get(result, 'ball_delivery_serial_number', '')) {
        const ballDeliverySerialNumber = get(result, 'ball_delivery_serial_number');

        if (Array.isArray(state.sessionDetails)) {
          state.sessionDetails = state.sessionDetails.map((item) => {
            if (item.ball_delivery_serial_number === ballDeliverySerialNumber) {
              return {
                ...item,
                ...result,
              };
            }
            return item;
          });
        }
      }
    },
    updateGroundTruthInProgress: (state) => {
      state.actionInProgress = true;
    },
    updateGroundTruthFailed: (state) => {
      state.error = {
        message: 'updateGroundTruthFailed',
      };
      state.actionInProgress = false;
    },
    setActionRecognitionTestDatasetFlagInGlobalState: (state, action) => {
      // action.payload should contain { ballDeliverySerialNumber, isActionRecognitionTestDataset }
      const result = get(action, 'payload', {});
      if (get(result, 'ballDeliverySerialNumber', '')) {
        const ballDeliverySerialNumber = get(result, 'ballDeliverySerialNumber');

        if (Array.isArray(state.sessionDetails)) {
          state.sessionDetails = state.sessionDetails.map((item) => {
            if (item.ball_delivery_serial_number === ballDeliverySerialNumber) {
              return {
                ...item,
                is_action_recognition_test_data: get(result, 'isActionRecognitionTestDataset', false),
              };
            }
            return item;
          });
        }
      }
    },
    setBallBounceDetectionTestDatasetFlagInGlobalState: (state, action) => {
      // action.payload should contain { ballDeliverySerialNumber, isBallBounceDetectionTestDataset }
      const result = get(action, 'payload', {});
      if (get(result, 'ballDeliverySerialNumber', '')) {
        const ballDeliverySerialNumber = get(result, 'ballDeliverySerialNumber');

        if (Array.isArray(state.sessionDetails)) {
          state.sessionDetails = state.sessionDetails.map((item) => {
            if (item.ball_delivery_serial_number === ballDeliverySerialNumber) {
              return {
                ...item,
                is_ball_bounce_detection_test_data: get(result, 'isBallBounceDetectionTestDataset', false),
              };
            }
            return item;
          });
        }
      }
    },
  },
});

export const {
  setPageNumber,
  getSessionsSuccess,
  getSessionsInProgress,
  getSessionsFailed,
  getSessionDetailsSuccess,
  getSessionDetailsInProgress,
  getSessionDetailsFailed,
  updateGroundTruthSuccess,
  updateGroundTruthInProgress,
  updateGroundTruthFailed,
  setActionRecognitionTestDatasetFlagInGlobalState,
  setBallBounceDetectionTestDatasetFlagInGlobalState,
} = groundTruthUpdatesSlice.actions;

export default groundTruthUpdatesSlice.reducer;
